import React, { useMemo } from 'react';
import barcode from './barcode.gif';
import { toCurrency, getGameTicketImage } from '../utils/helper';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

export default function TicketTemplate1({ transaction, displayName }) {
  const digitsOnly = transaction.id.replace(/[^0-9]/g, '');
  const transactionId = transaction.id.replace(/-/g, '');
  const formattedTransactionId =
    transactionId.substring(4, 8) +
    '-' +
    transactionId.substring(8, 12) +
    '-' +
    transactionId.substring(12, 16) +
    '-' +
    transactionId.substring(16, 20);
  const drawId = dayjs(transaction.drawDate)
    .diff(dayjs('2025-04-12'), 'day')
    .toString()
    .padStart(7, '0');
  const groupedBets = useMemo(() => {
    // First group by bettingOption and combination length
    const byBettingOption = {};

    for (const bet of transaction.bets) {
      const key =
        bet.bettingOption === 'ramble'
          ? `${bet.bettingOption}-${
              [...new Set(bet.combination.split(''))].length
            }`
          : bet.bettingOption;

      if (!byBettingOption[key]) {
        byBettingOption[key] = [];
      }
      byBettingOption[key].push(bet);
    }

    // Then group each betting option's bets into groups of 6
    const result = {};

    byBettingOption['ramble'] = [
      ...(byBettingOption['ramble-3'] || []),
      ...(byBettingOption['ramble-2'] || []),
    ];
    delete byBettingOption['ramble-3'];
    delete byBettingOption['ramble-2'];
    if (!byBettingOption['ramble'].length) {
      delete byBettingOption['ramble'];
    }
    for (const [option, optionBets] of Object.entries(byBettingOption)) {
      result[option] = [];
      for (let i = 0; i < optionBets.length; i += 6) {
        result[option].push(optionBets.slice(i, i + 6));
      }
    }

    return result;
  }, [transaction]);

  return (
    <div className='grid max-h-0 overflow-hidden'>
      <div
        id='print-ticket'
        className='w-[350px] text-sm flex flex-col gap-4 bg-white font-bitMatrix antialiased'
      >
        {Object.entries(groupedBets).map(([option, bets]) => (
          <div key={option} className='flex flex-col'>
            {bets.map((betGroup, index) => (
              <div key={index} className='flex flex-col mb-2 tickets bg-white'>
                <img
                  className='h-14 mx-auto'
                  src={getGameTicketImage(betGroup[0].gameSettingsId)}
                />
                <div className='text-center font-bitMatrixWide text-base -m-2 mb-2'>
                  {option === 'ramble' && <div>Rambolito</div>}
                </div>
                {betGroup.map((bet, index) => (
                  <div key={index}>
                    <div className='flex flex-col'>
                      <div className='flex justify-between text-lg items-center'>
                        <div className='text-left'>
                          <span className='uppercase font-bitMatrixWide text-base mr-4'>
                            {String.fromCharCode(97 + index)}:
                          </span>
                          <span className='font-bitMatrixWide text-sm font-bold'>
                            {bet.combination}
                          </span>
                          {option === 'ramble' &&
                            transaction.bets[0].gameSettingsId === 1 && (
                              <span className='text-base ml-6 font-light font-bitMatrix'>
                                x
                                {[...new Set(bet.combination.split(''))]
                                  .length === 3
                                  ? '6'
                                  : '3'}
                              </span>
                            )}
                        </div>
                        <div className='flex flex-col gap-1 items-end font-bitMatrixWide text-[12px]'>
                          {bet.bettingOption === 'S/O' ? (
                            <span>S/O</span>
                          ) : (
                            <span>P{toCurrency(bet.amount)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className='flex flex-col'>
                  <div className='flex justify-between items-baseline mr-2'>
                    <span className='uppercase'>
                      {transaction.id.substring(0, 4)}{' '}
                      {`${digitsOnly.substring(0, 3)}-${digitsOnly.substring(
                        3,
                        7
                      )}-${digitsOnly.substring(7, 8)}`}
                    </span>
                    <span>
                      {102000 + +drawId}{' '}
                      {dayjs(transaction.bets[0].createdAt).format(
                        'DD-MMM-YYYY HH:mm'
                      )}
                    </span>
                  </div>
                  <div className='flex items-baseline justify-between'>
                    <span className='ml-8'>Ticket Price:</span>
                    <span className='font-bitMatrixWide text-[12px]'>
                      P
                      {toCurrency(
                        betGroup
                          .filter((bet) => bet.bettingOption !== 'S/O')
                          .reduce((acc, bet) => +acc + +bet.amount, 0)
                      )}
                    </span>
                  </div>
                  <div className='flex items-baseline uppercase gap-1'>
                    <span className='capitalize'>Draw </span> {drawId}{' '}
                    {dayjs(transaction.drawDate).format('ddd DD-MMM-YYYY') +
                      ' ' +
                      dayjs(transaction.drawTime, 'HH:mm').format('H:mm')}
                  </div>
                  <div className='flex items-baseline justify-between'>
                    <span>{dayjs().format('DD-MMM-YYYY HH:mm')}</span>
                    <span className='uppercase'>
                      {transaction.id.substring(0, 4)}-
                      {transaction.id.substring(4, 8)}
                    </span>
                  </div>
                </div>
                <img
                  className='w-3/4 mt-6 mx-auto h-12'
                  src={barcode}
                  loading='lazy'
                  decoding='async'
                />
                <span className='text-center text-sm font-bitMatrix mt-4'>
                  {formattedTransactionId.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
